
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  Episode,
  Genre,
  Movie,
  Season,
  TvShow,
  VoDGenre
} from "@/services/apiService/api.types";
import { Action, Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import DateTimeSection from "@/components/DateTimeSection.vue";
import SpatialNavigation from "spatial-navigation-js";
import {
  VodPageAction,
  VodPageGetter,
  VodPageMutation,
  VoDType
} from "@/components/VoD/types";
import apiService from "@/services/apiService/apiService";
import store from "@/store";
import { AlertAction, AlertType } from "@/components/alert/types";
import apiStorageInterface from "@/services/apiStorageInterface";
import storageService from "@/services/storageService/storageService";
import { LocalStorageItems } from "@/helpers/constants";
import { EventBus } from "@/main";
import PlayerBase from "@/components/player/components/PlayerBase.vue";
import { OverlayMutation } from "@/components/overlay/types";
import VoDCard from "@/components/VoD/component/VoDCard.vue";
import VoDModalSwipers from "@/components/VoD/component/VoDModalSwipers.vue";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { isTvShow, scrollToPage } from "@/helpers/utils";
import EpisodeInfoModal from "@/components/VoD/component/EpisodeInfoModal.vue";
import { RouteName } from "@/router/types";
import { HomePageGetter, IndexData } from "@/components/homePage/types";
import dayjs from "dayjs";

@Component({
  components: {
    PlayerBase,
    DateTimeSection,
    "vod-card": VoDCard,
    "vod-modal-swipers": VoDModalSwipers,
    "vod-info-modal": VoDInfoModal,
    EpisodeInfoModal
  }
})
export default class VoDInfoModal extends Vue {
  @Action(VodPageAction.UpdateTvShowsByGenre, {
    namespace: Namespace.Vod
  })
  updateTvShowsByGenre!: () => void;

  @Action(VodPageAction.UpdateMoviesByGenre, {
    namespace: Namespace.Vod
  })
  updateMoviesByGenre!: () => void;

  @Action(VodPageAction.UpdateClosePlayer, {
    namespace: Namespace.Vod
  })
  updateClosePlayer!: (status: boolean) => void;

  @Mutation(VodPageMutation.setShowVodInfoModal, {
    namespace: Namespace.Vod
  })
  setShowVodInfoModal!: (show: boolean) => void;

  @Mutation(OverlayMutation.SetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  setActiveVodItem!: (vod: Movie | Episode | null) => void;

  @Mutation(OverlayMutation.SetTvShowMeta, {
    namespace: Namespace.Overlay
  })
  setTvShowMeta!: (meta: Season[] | null) => void;

  @Getter(VodPageGetter.GetViewedVodType, {
    namespace: Namespace.Vod
  })
  getViewedVodType?: VoDType;

  @Getter(VodPageGetter.GetInfoModalVodId, {
    namespace: Namespace.Vod
  })
  getInfoModalVodId!: number;

  @Getter(VodPageGetter.GetRefocusCardId, {
    namespace: Namespace.Vod
  })
  getRefocusCardId!: string;

  @Getter(VodPageGetter.GetClosePlayer, { namespace: Namespace.Vod })
  getClosePlayer!: boolean;

  @Getter(VodPageGetter.GetShowEpisodeInfoModal, { namespace: Namespace.Vod })
  getShowEpisodeInfoModal!: boolean;

  @Getter(VodPageGetter.GetActiveEpisode, { namespace: Namespace.Vod })
  getActiveEpisode!: Episode | null;

  @Getter(HomePageGetter.GetPlayedWidgetIndex, {
    namespace: Namespace.HomePage
  })
  getPlayedWidgetIndex?: IndexData;

  @Getter(VodPageGetter.GetTvShowsByGenre, {
    namespace: Namespace.Vod
  })
  getTvShowsByGenre!: VoDGenre[];

  @Getter(VodPageGetter.GetMoviesByGenre, {
    namespace: Namespace.Vod
  })
  getMoviesByGenre!: VoDGenre[];

  vodItem: Movie | TvShow = {
    id: 0,
    name: "",
    created_at: dayjs().unix(),
    updated_at: dayjs().unix(),
    provider_id: 0,
    file_name: ""
  };

  isFavorite = false;

  scrollFuncRef = scrollToPage;

  async mounted(): Promise<void> {
    SpatialNavigation.resume();
    SpatialNavigation.focus("#backBtnVodModal");
    await this.fetchVoD();
    if (isTvShow(this.vodItem) && this.vodItem.seasons !== undefined) {
      this.setTvShowMeta(this.vodItem.seasons);
    }
    const favMovies = await apiStorageInterface.fetchFavoriteMovies();
    const favTvShows = await apiStorageInterface.fetchFavoriteTvShows();

    switch (this.getViewedVodType) {
      case VoDType.Movies:
        this.isFavorite = !!favMovies.find(
          (movie) => movie.id === this.vodItem.id
        );
        break;
      case VoDType.TvShows:
        this.isFavorite = !!favTvShows.find(
          (tvShow) => tvShow.id === this.vodItem.id
        );
        break;
    }
    EventBus.$on("player-mounted", () => {
      this.positionPlayer();
    });
  }

  get activeVoDSplash(): string {
    return this.vodItem?.media ? this.vodItem.media.url : this.imgPlaceholder;
  }

  get imgPlaceholder(): string {
    return require("/src/assets/images/square-image-placeholder.jpg");
  }

  get vodYear(): string {
    return this.vodItem.year ? this.vodItem.year : "";
  }

  get vodTitle(): string {
    return this.vodItem ? this.vodItem.name : "";
  }

  get vodDescription(): string {
    return this.vodItem.description ? this.vodItem.description : "";
  }

  get vodGenres(): Genre[] {
    if (this.vodItem.genres) {
      return this.vodItem.genres;
    }

    return [];
  }

  get vodDirector(): string {
    if (this.vodItem.director) {
      return this.vodItem.director;
    }

    return "";
  }

  get vodCast(): string {
    if (this.vodItem.cast) {
      return this.vodItem.cast;
    }

    return "";
  }

  async fetchVoD(): Promise<void> {
    switch (this.getViewedVodType) {
      case VoDType.TvShows:
        this.vodItem = await apiService.getTvShow(this.getInfoModalVodId);
        break;
      case VoDType.Movies:
        this.vodItem = await apiService.getMovie(this.getInfoModalVodId);
        break;
    }
  }

  get showPlayBtn(): boolean {
    return this.getViewedVodType === VoDType.Movies;
  }

  get isInVoDPackage(): boolean {
    switch (this.getViewedVodType) {
      case VoDType.Movies:
        for (const genre of this.getMoviesByGenre) {
          for (const movie of genre.latestMovies!) {
            if (movie.id === this.vodItem.id) {
              return true;
            }
          }
        }

        return false;
      case VoDType.TvShows:
        for (const genre of this.getTvShowsByGenre) {
          for (const tvShow of genre.latestTvShows!) {
            if (tvShow.id === this.vodItem.id) {
              return true;
            }
          }
        }
        return false;
      default:
        return false;
    }
  }

  closeOverlay(): void {
    if (!this.getShowEpisodeInfoModal) {
      if (this.getPlayedWidgetIndex) {
        this.$router.push({ name: RouteName.Home });
      }
      if (document.getElementById(this.getRefocusCardId)) {
        SpatialNavigation.focus("#" + this.getRefocusCardId);
      } else {
        //can happen if we remove VoD from favorites and therefore the VoD card from which we opened the modal is no longer present
        EventBus.$emit("cantRefocus");
      }

      if (this.getClosePlayer) return this.updateClosePlayer(false);
      if (!this.getClosePlayer) {
        this.setShowVodInfoModal(false);
      }
      SpatialNavigation.resume();
    }
  }

  async addToFavorites(): Promise<void> {
    const favMovies = await apiStorageInterface.fetchFavoriteMovies();
    const favTvShows = await apiStorageInterface.fetchFavoriteTvShows();
    try {
      switch (this.getViewedVodType) {
        case VoDType.Movies:
          if (favMovies.find((movie) => movie.id === this.vodItem.id)) {
            await apiService.removeMovieFromFavorites(this.vodItem.id);
            this.isFavorite = false;
          } else {
            await apiService.setMovieAsFavorite(this.vodItem.id);
            this.isFavorite = true;
          }

          storageService.removeItem(LocalStorageItems.FavoriteMovies);
          await apiStorageInterface.fetchFavoriteMovies();
          await this.updateMoviesByGenre();
          break;
        case VoDType.TvShows:
          if (favTvShows.find((tvShow) => tvShow.id === this.vodItem.id)) {
            await apiService.removeTvShowFromFavorites(this.vodItem.id);
            this.isFavorite = false;
          } else {
            await apiService.setTvShowAsFavorite(this.vodItem.id);
            this.isFavorite = true;
          }

          storageService.removeItem(LocalStorageItems.FavoriteTvShows);
          await apiStorageInterface.fetchFavoriteTvShows();
          await this.updateTvShowsByGenre();
          break;
      }

      EventBus.$emit("VodFavUpdate");
    } catch (error) {
      switch (error.response.status) {
        case 404:
          await store.dispatch(
            `${Namespace.Alert}/${AlertAction.UpdateAlert}`,
            {
              show: true,
              message: error.response.message,
              alertType: AlertType.Danger
            }
          );
          break;
        case 500:
          await store.dispatch(
            `${Namespace.Alert}/${AlertAction.UpdateAlert}`,
            {
              show: true,
              message: "Internal server error",
              alertType: AlertType.Danger
            }
          );
          break;
      }
    }
  }

  handlePlay(): void {
    //only Movie or TV Show Episode will have a play button enabled since a Tv Show must select an episode to view
    this.setActiveVodItem(this.vodItem as Movie);
    this.updateClosePlayer(true);
  }

  focusOnExtraContent(e: KeyboardEvent): void {
    switch (keyCodeFinder(e.keyCode)) {
      case KeySet.KEY_DOWN:
        EventBus.$emit("focusExtraContent");
        break;
      default:
        SpatialNavigation.resume();
        break;
    }
  }

  positionPlayer(): void {
    if (this.getClosePlayer) {
      // get the position of the first modal
      const modal1 = document.getElementById("vodInfoModal");
      const modal1Rect = modal1!.getBoundingClientRect();
      const modal1Top = modal1Rect.top + window.pageYOffset;

      // get the scroll position inside the first modal
      const modal1ScrollTop = modal1!.scrollTop;

      // set the position of the second modal
      let modal2 = document.getElementById("playerContainer");
      modal2!.style.top = modal1Top + modal1ScrollTop + "px";
    }
  }

  @Watch("getClosePlayer")
  handlePlaybackUpdate(newVal: boolean): void {
    if (!newVal && !isTvShow(this.vodItem)) {
      SpatialNavigation.focus("#playBtn");
    }
  }

  @Watch("getShowEpisodeInfoModal")
  handleEpisodeModal(newVal: boolean): void {
    if (newVal && this.getActiveEpisode !== null) {
      this.scrollFuncRef("backBtnEpisodeModal");
    }
  }

  beforeDestroy(): void {
    SpatialNavigation.disable("@vodOverlay");
    this.setTvShowMeta(null);
  }
}
